/**
 * ProfessocialCore ist der Einstiegspunkt für die meisten Komponenten. Hier werden die Routen mit den jerweiligen Modulen verbunden, Pfade und Konfigurationen übergeben.
 * Navigation und Subnavigation werden bereit gestellt.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, useLocation, Redirect, Switch } from 'react-router-dom'

import { CssBaseline, StyledEngineProvider } from '@mui/material'
import { ThemeProvider, Theme } from '@mui/material/styles'
import { ThemeProvider as StylesThemeProvider } from '@mui/styles'

import * as commonActions from '../store/common/common.actions'

import SubsidiaryApp from './Subsidiary/SubsidiaryApp'
import UserApp, { navigationUserListData } from './User/UserApp'
import EditorApp, { navigationEditorListData } from './Editor/EditorApp'
import AdminApp, { navigationAdminListData } from './Admin/AdminApp'

import Header from '../shared/components/HeaderComponents/Header'

import { Languages, Navigation } from '@professocial/professocialTypes/types/commonTypes'
import ProfessocialExternalLinks from '@professocial/professocialExternalLinks/src/screens/ProfessocialExternalLinks'
import { CoreConfiguration, ExternalLinksConfiguration } from '@professocial/professocialTypes/types/configurationTypes'

import getAppTheme from '../defaults/getAppTheme'
import SettingsApp from './Settings/SettingsApp'
import NavigationBar from '../shared/components/NavigationComponents/Navigation'
import { getUser, namedRequestInProgress } from '../store/common/common.selectors'
import {
  acceptAnnouncementInitiated,
  getCurrentUserInitiated, getPageIdentifiersForUserInitiated,
  getSmPublishAuthTokenInitiated,
  setDevelopmentSystem,
  setLanguage,
  setUser,
  userLoginInitiated,
} from '../store/common/common.actions'
import Logout from './Logout'
import WithConnectRoute from './withConnectRoute'
import WithAuthRoute from './withAuthRoute'
import { CoreRootState } from '../store/coreRoot.reducer'
import Submenu, { SubmenuData } from '@professocial/professocialFormComponents/FormComponents/Submenu'
import getSubmenu from '../util/getSubmenu'
import UserWizard from './User/UserWizard/UserWizard'
import UserQuiz from './User/UserQuiz/UserQuiz'
import UserCommitment from './User/UserCommitment/UserCommitment'
import { UserSettings } from '@professocialApi/professocial-core-frontend-api'
import { GetPageIdentifiersForUserRequest } from '@professocialApi/professocial-core-frontend-api/apis/UserHasPageApi'
import { getActiveAnnouncement } from '../store/common/common.selectors'
import { getActiveAnnouncementInitiated } from '../store/common/common.actions'
import AnnouncementHeader from './User/UserAnnouncement/AnnouncementHeader'
import AnnouncementDialog from './User/UserAnnouncement/AnnouncementDialog'

export interface Props {
  coreConfiguration?: CoreConfiguration;
}

const ProfessocialCore: React.FC<Props> = (props) => {
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  // const activeAnnouncement = useSelector(getActiveAnnouncement)
  const location = useLocation()

  const dispatchSetDevelopmentSystem = useCallback((param: boolean) => dispatch(setDevelopmentSystem(param)), [dispatch])
  const dispatchSetLanguage = useCallback((param: Languages[]) => dispatch(setLanguage(param)), [dispatch])
  const dispatchGetUser = useCallback(() => dispatch(getCurrentUserInitiated()), [dispatch])
  const dispatchGetToken = useCallback(() => dispatch(getSmPublishAuthTokenInitiated()), [dispatch])
  const dispatchSetUser = useCallback((params: UserSettings) => dispatch(setUser(params)), [dispatch])
  const dispatchGetPageIdentifiersForUser = useCallback((params: GetPageIdentifiersForUserRequest) => dispatch(getPageIdentifiersForUserInitiated(params)), [dispatch])

  const processingLogin: boolean = useSelector((state: CoreRootState) => namedRequestInProgress(state, userLoginInitiated.type))

  const [theme, setTheme] = useState<Theme>(props.coreConfiguration?.theme || getAppTheme())
  const [navigationList, setNavigationList] = useState<Navigation[]>([])
  const [naviChanged, setNaviChanged] = useState(true)
  const [submenuData, setSubmenuData] = useState<SubmenuData[] | undefined>()
  const [wizardCompleted, setWizardCompleted] = useState<boolean>(true)
  const [quizCompleted, setQuizCompleted] = useState<boolean>(true)
  const [isLogout, setIsLogout] = useState<boolean>(false)

  const storeAnnouncement = useSelector(getActiveAnnouncement)
  const [activeAnnouncement, setActiveAnnouncement] = useState(storeAnnouncement)

  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState<boolean>(true)

  const isTermsOfUsePage = location.pathname.includes('termsOfUse')
  const isHelpPage = location.pathname.includes('/user/help')
  // ExternalLinksConfiguration
  const externalLinksConfiguration: ExternalLinksConfiguration | undefined = props.coreConfiguration?.externalLinks

  // Action to fetch the active announcement
  const dispatchGetCurrentActiveAnnouncement = useCallback(() => {
    dispatch(getActiveAnnouncementInitiated())
  }, [dispatch])

  const dispatachAcceptAnnouncement = useCallback(() => {
    dispatch(acceptAnnouncementInitiated())
  }, [dispatch])

  const handleNaviChange = useCallback((naviChanged: boolean) => {
    setNaviChanged(naviChanged)
  }, [])

  const handleWizardCompleted = useCallback(() => {
    setWizardCompleted(true)
  }, [])

  const handleQuizCompleted = useCallback(() => {
    setQuizCompleted(true)
  }, [])

  const handleLogout = useCallback(() => {
    setIsLogout(true)
  }, [])

  // Fetch the active announcement when the component mounts
  useEffect(() => {
    dispatchGetCurrentActiveAnnouncement()
  }, [dispatchGetCurrentActiveAnnouncement])

  useEffect(() => {
    setActiveAnnouncement(storeAnnouncement)
  }, [storeAnnouncement])

  const configUser = props.coreConfiguration?.user
  const configLanguage = props.coreConfiguration?.language

  useEffect(() => {
    if (configUser) {
      dispatchSetUser(configUser)
    } else {
      dispatchGetUser()
    }
    configLanguage && dispatchSetLanguage(configLanguage)
  }, [configUser, configLanguage, dispatchSetUser, dispatchGetUser, dispatchSetLanguage])

  useEffect(() => {
    dispatchSetDevelopmentSystem(props.coreConfiguration?.developmentSystem || false)
  }, [props.coreConfiguration?.developmentSystem, dispatchSetDevelopmentSystem])

  useEffect(() => {
    if (location.pathname.includes('settings') && location.pathname.includes('privacy')) {
      setNaviChanged(false)
    }
  }, [location.pathname])

  useEffect(() => {
    props.coreConfiguration?.theme && setTheme(props.coreConfiguration.theme)
    props.coreConfiguration?.theme && dispatch(commonActions.setTheme(props.coreConfiguration.theme))
  }, [props.coreConfiguration?.theme, dispatch])

  useEffect(() => {
    // zunächst aktuellen JWT abholen
    if (user && user.role !== 'ADMIN' && !isLogout) {
      dispatchGetToken()
    }

    // user network licenses activation setLicensePermissions
    dispatch(commonActions.setLicensePermissions({
      facebook: !!user?.mandator?.licensePermissions?.canUseFacebook,
      instagram: !!user?.mandator?.licensePermissions?.canUseInstagram,
      linkedIn: !!user?.mandator?.licensePermissions?.canUseLinkedIn,
      googleMyBusiness: !!user?.mandator?.licensePermissions?.canUseGoogleMyBusiness,
    }))

    if (user && !user.finishedWizard && user.role === 'USER') {
      setWizardCompleted(false)
    }

    if (user && !user.finishedQuiz && user.role === 'USER') {
      setQuizCompleted(false)
    }

    if (user && user.id) {
      if (user.role === 'USER') {
        setNavigationList(navigationUserListData)
      }
      if (user.role === 'EDITOR') {
        setNavigationList(navigationEditorListData)
      }
      if (user.role === 'ADMIN') {
        setNavigationList(navigationAdminListData)
      }
      if (user.role === 'SUBSIDIARY') {
        setNavigationList(navigationEditorListData)
      }
    }
    user && user.id && !isLogout && dispatchGetPageIdentifiersForUser({ userEntityId: user.id })
    user && user.localeCode && window.localStorage.setItem('language', user.localeCode)
  }, [user, isLogout, dispatchGetToken, dispatch, dispatchGetPageIdentifiersForUser])

  useEffect(() => {
    setSubmenuData(getSubmenu(location.pathname))
  }, [location])

  const handleAcceptAnnouncement = useCallback(() => {
    dispatachAcceptAnnouncement()
    setAnnouncementDialogOpen(false)
  }, [dispatachAcceptAnnouncement])

  return (
    <>
      <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons"/>
      <ThemeProvider theme={theme}>
        <StylesThemeProvider theme={theme}>
          <StyledEngineProvider injectFirst>
            <>
              <CssBaseline />
              {user &&
              user.role === 'USER' &&
              wizardCompleted &&
              quizCompleted &&
              activeAnnouncement && (
                <>
                  {/* Announcement header is rendered at the top and is not dismissible if isHeaderVisible is true */}
                  {activeAnnouncement.isHeaderVisible && (
                    <AnnouncementHeader announcement={activeAnnouncement} />
                  )}

                  {/* Announcement dialog is always rendered as a modal and can be dismissed */}
                  {!activeAnnouncement.isApproved && !isTermsOfUsePage && !isHelpPage && user.commitmentsConfirmed && (
                    <AnnouncementDialog
                      announcement={activeAnnouncement}
                      open={announcementDialogOpen}
                      onClose={handleAcceptAnnouncement}
                    />
                  )}
                </>
              )
              }
              {user && (
                <>
                  <Header user={user} logo={props.coreConfiguration?.logo} setNaviChange={handleNaviChange} />
                  {((user.finishedWizard && user.commitmentsConfirmed && (wizardCompleted || quizCompleted)) || user.role !== 'USER') && (
                    <NavigationBar
                      user={user}
                      navigationList={navigationList}
                      setNaviChange={handleNaviChange}
                      naviChanged={naviChanged}
                    />
                  )}
                  <Submenu data={submenuData} config={props.coreConfiguration} />
                  <Switch>
                    <Route path={`/${user.role.toLowerCase()}/termsOfUse`}>
                      {externalLinksConfiguration && (
                        <ProfessocialExternalLinks externalLinksConfiguration={externalLinksConfiguration} user={user} />
                      )}
                    </Route>
                    {user.role === 'USER' && !user.commitmentsConfirmed && (
                      <Route path="/">
                        <UserCommitment />
                      </Route>
                    )}
                    {(user.role === 'USER' && !user.finishedQuiz || !quizCompleted) && (
                      <Route path="/">
                        <UserQuiz setQuizCompleted={handleQuizCompleted} />
                      </Route>
                    )}
                    {(user.role === 'USER' && !user.finishedWizard && quizCompleted || !wizardCompleted) && (
                      <Route path="/">
                        <UserWizard setWizardCompleted={handleWizardCompleted} />
                      </Route>
                    )}
                    {!isLogout && (
                      <Route exact path="/">
                        {user && navigationList && navigationList[0] && (
                          <Redirect to={`/${user.role.toLowerCase()}/${navigationList[0].key}/`} />
                        )}
                      </Route>
                    )}
                    <Route path={`/${user.role.toLowerCase()}/privacy`}>
                      {externalLinksConfiguration && (
                        <ProfessocialExternalLinks externalLinksConfiguration={externalLinksConfiguration} user={user} />
                      )}
                    </Route>
                    <WithConnectRoute path="/subsidiary" component={SubsidiaryApp} />
                    <WithConnectRoute path="/user" component={UserApp} />
                    <WithAuthRoute path="/editor" component={EditorApp} />
                  </Switch>
                  <Route path="/admin" component={AdminApp} />
                  <Route path={`/${user.role.toLowerCase()}/settings`}>
                    <SettingsApp user={user} />
                  </Route>
                  <Route path={`/${user.role.toLowerCase()}/logout`}>
                    <Logout proceedLogout={handleLogout} />
                  </Route>
                </>
              )}
            </>
          </StyledEngineProvider>
        </StylesThemeProvider>
      </ThemeProvider>
    </>
  )
}

export default ProfessocialCore
